// DialogProvider — renders a single modal that any code can show via
// confirm() / prompt() / alertBox() from lib/dialogs.js.
//
// Mounted once at the app root (App.js, inside the AuthProvider). Maintains
// a queue of pending dialog requests so two near-simultaneous calls don't
// step on each other (e.g. a button handler that does a confirm + a follow-
// up prompt).

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { _registerDialogHandler } from '../lib/dialogs';

export default function DialogProvider({ children }) {
  // Queue of { spec, resolve } entries. Front of queue is shown.
  const [queue, setQueue] = useState([]);
  const current = queue[0]?.spec || null;
  const resolveCurrent = useRef(null);
  resolveCurrent.current = queue[0]?.resolve || null;

  // Prompt input — local to the modal, reset whenever a new prompt opens.
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (current?.type === 'prompt') setInputValue(current.defaultValue ?? '');
  }, [current]);

  // Register the handler so confirm()/prompt()/alertBox() reach this
  // component. The handler returns a promise; we capture its resolver in
  // the queue entry so the modal's button clicks can fulfil it.
  useEffect(() => {
    _registerDialogHandler((spec) => new Promise((resolve) => {
      setQueue((q) => [...q, { spec, resolve }]);
    }));
    return () => _registerDialogHandler(null);
  }, []);

  const dismiss = useCallback((value) => {
    const r = resolveCurrent.current;
    if (r) r(value);
    setQueue((q) => q.slice(1));
  }, []);

  // Keyboard: Esc cancels, Enter confirms (if no input or input is single-line).
  useEffect(() => {
    if (!current) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        dismiss(current.type === 'prompt' ? null : false);
      } else if (e.key === 'Enter') {
        // Don't hijack Enter inside a textarea
        if (e.target.tagName === 'TEXTAREA') return;
        e.preventDefault();
        if (current.type === 'prompt')      dismiss(inputValue);
        else if (current.type === 'confirm') dismiss(true);
        else                                 dismiss(undefined);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [current, inputValue, dismiss]);

  return (
    <>
      {children}
      {current && (
        <DialogModal
          spec={current}
          inputValue={inputValue}
          setInputValue={setInputValue}
          onCancel={() => dismiss(current.type === 'prompt' ? null : false)}
          onConfirm={() => {
            if (current.type === 'prompt')   dismiss(inputValue);
            else if (current.type === 'alert') dismiss();
            else                              dismiss(true);
          }}
        />
      )}
    </>
  );
}

function DialogModal({ spec, inputValue, setInputValue, onCancel, onConfirm }) {
  const inputRef = useRef(null);

  // Auto-focus the input on prompt, OK button on confirm/alert
  useEffect(() => {
    if (spec.type === 'prompt') {
      inputRef.current?.focus();
      inputRef.current?.select();
    } else {
      // small timeout so the OK button is rendered before we focus it
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isAlert   = spec.type === 'alert';
  const isPrompt  = spec.type === 'prompt';
  const isDanger  = !!spec.danger;
  const title     = spec.title || (isAlert ? 'Notice' : isPrompt ? 'Input required' : 'Confirm');
  const okLabel   = spec.confirmLabel || (isAlert ? 'OK' : isPrompt ? 'OK' : 'Confirm');
  const cancelLabel = spec.cancelLabel || 'Cancel';

  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 10000, padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dialog-title"
        style={{
          background: 'white', borderRadius: 14, padding: 22,
          width: '100%', maxWidth: 440,
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        }}
      >
        <h3
          id="dialog-title"
          style={{
            margin: '0 0 12px', fontSize: 17, fontWeight: 600,
            color: isDanger ? '#b91c1c' : '#0f172a',
          }}
        >
          {title}
        </h3>

        <div style={{ fontSize: 14, color: '#334155', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
          {spec.message}
        </div>

        {isPrompt && (
          <div style={{ marginTop: 12 }}>
            {spec.inputLabel && (
              <label style={{
                display: 'block', fontSize: 11, fontWeight: 700, color: '#546e7a',
                textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4,
              }}>
                {spec.inputLabel}
              </label>
            )}
            <input
              ref={inputRef}
              className="form-control"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={spec.placeholder || ''}
              autoComplete="off"
              style={{ width: '100%' }}
            />
          </div>
        )}

        <div style={{
          marginTop: 18, display: 'flex', gap: 8, justifyContent: 'flex-end',
        }}>
          {!isAlert && (
            <button onClick={onCancel} className="btn btn-outline">{cancelLabel}</button>
          )}
          <button
            ref={isAlert || !isPrompt ? inputRef : undefined}
            onClick={onConfirm}
            className={isDanger ? 'btn btn-danger' : 'btn btn-primary'}
          >
            {okLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
